





























































































import {
  defineComponent,
  PropType,
  ref,
  useContext,
  computed
} from '@nuxtjs/composition-api';
import { useReview, useTranslations } from '@vue-storefront/novulo';
import useWriteReviewDrawerOpener from '~/hooks/useWriteReviewDrawerOpener';
import useWindowResize from '~/hooks/useResizeWindow';
import { FeatureReview } from '~/components/atoms/Review.vue';
import { useVSFContext } from '@vue-storefront/core';

export type ReviewAnswerType = {
  interpretation_score: number;
  answer_description: string;
  answer_text: string;
  response_modeltype: number;
  question_phrasing: string;
  question_id: number;
};

export type ReviewType = {
  reviewed_by: string;
  average_score: number;
  review_answers: ReviewAnswerType[];
};

export default defineComponent({
  name: 'Reviews',
  props: {
    rating: {
      type: Number,
      default: 0
    },
    totalReviews: {
      type: Number,
      default: 0
    },
    reviews: {
      type: Array as PropType<ReviewType[]>,
      default: () => []
    },
    productSlug: {
      type: String,
      default: ''
    },
    productId: {
      type: Number,
      default: 0
    },
    productSku: {
      type: String,
      default: ''
    }
  },
  setup(props) {
    const { getReviews, reviewResult, getWriteReviewEssentials } = useReview();
    const { toggleWriteReviewDrawer } = useWriteReviewDrawerOpener();
    const { i18n } = useContext();
    const vsfContext = useVSFContext();
    const { translate } = useTranslations();

    const { isDesktop } = useWindowResize();

    const hasNewReviewsOption = computed(() => {
      const wt = vsfContext.$novulo.config.state.getWt();
      // only show new review option for KE Germany
      const isKEDE = wt === 21;
      return isKEDE;
    });
    const isShowingNewReviews = ref(hasNewReviewsOption.value);

    const handleNewReviewClick = () => {
      getWriteReviewEssentials(props.productId);
      toggleWriteReviewDrawer(true);
    };

    const formatFeatureReviews = (reviewanswers) => {
      const featuresReviews: FeatureReview[] = [];

      if (reviewanswers) {
        reviewanswers.forEach((answer) => {
          if (
            answer.response_modeltype === 0 &&
            answer.question_phrasing !== i18n.t('Recommended')
          ) {
            featuresReviews.push({
              feature: answer.question_phrasing,
              percentage: answer.interpretation_score * 20,
              ratingText: answer.answer_description
            });
          }
        });
      }
      return featuresReviews;
    };

    return {
      reviewResult,
      isDesktop,
      hasNewReviewsOption,
      isShowingNewReviews,
      getReviews,
      handleNewReviewClick,
      formatFeatureReviews,
      translate
    };
  },
  data() {
    const current = ref(1);
    const isFirstCall = true;
    const PAGES_VISIBLE = 3;
    return { isFirstCall, current, PAGES_VISIBLE };
  },
  computed: {
    reviewsShown() {
      if (this.isFirstCall && this.reviews[1])
        return [this.reviews[0]].concat(this.reviews[1]);
      if (this.current.value === 1) return this.reviews;
      return this.reviewResult;
    },
    totalPages() {
      return Math.ceil(this.totalReviews / 5);
    }
  },
  methods: {
    async loadReviews(value) {
      this.current.value = value;
      const element = document.querySelector('#reviewsSection');
      if (element) {
        const yOffset = -200;
        const scrollPosition =
          element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
      }
      await this.getReviews({ productId: this.productId, page: value });
    },
    reviewerName(reviewedBy = ''): string {
      if (!reviewedBy) return '';
      return (
        reviewedBy
          // trim everything before first parenthesis
          .replace(/^[^(]*\(/, '')
          // trim everything after last parenthesis
          .replace(/\)[^(]*$/, '')
          // split between parenthesis
          .split(/\)[^(]*\(/)[0]
      );
    },
    reviewerDate(date: string): string {
      if (!date) return '';
      const dateTemp = new Date(date);

      const day = dateTemp.getUTCDate();
      const month = dateTemp.getUTCMonth() + 1;
      const year = dateTemp.getUTCFullYear();

      return `${day}-${month}-${year}`;
    },
    reviewTitle(reviewanswers) {
      return (
        reviewanswers.find(
          (a: ReviewAnswerType) =>
            a.response_modeltype === 1 && a.question_id === 2810
        )?.answer_text || ''
      );
    },
    reviewDescription(reviewanswers) {
      return (
        reviewanswers.find(
          (a: ReviewAnswerType) =>
            a.response_modeltype === 1 && a.question_id === 2811
        )?.answer_text || ''
      );
    }
  }
});
